import React from 'react';
import {
  CreativeOverviewStateContentState,
  CreativeOverviewStateContentProps,
  CreativeOverviewStateContentModel
} from './CreativeOverviewStateContentModel';
import classNames from 'classnames/bind';
import styles from './creativeOverviewStateContent.module.scss';
import i18n from 'i18next';
import Tags from 'components/common/Tags/Tags';

import { CreativeList } from 'components/CampaignCreativeList/CreativeList';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { StateDataContext } from 'containers/Common/StateDataContext';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import { CreativeManagementStateContext } from './CreativeManagementStateContext';

const BIND_BLOCKED_TIP_ID = 'bindCreativeBlockedTip';
const CREATE_BLOCKED_TIP_ID = 'createCreativeBlockedTip';

export class CreativeOverviewStateContent extends React.Component<
  CreativeOverviewStateContentProps,
  CreativeOverviewStateContentState
> {
  handler: number;
  cssClassNames: any;

  constructor(props) {
    super(props);
    this.cssClassNames = classNames.bind(styles);
    this.handler = this.props.model.event.add((model) => {
      this.setState(model.state);
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.model !== this.props.model) {
      prevProps.model.event.remove(this.handler);
      this.handler = this.props.model.event.add((model) => {
        this.setState(model.state);
      });
    }
  }

  componentWillUnmount() {
    this.props.model.event.remove(this.handler);
  }

  getAddCreativeOprations(model: CreativeOverviewStateContentModel) {
    if (model.isReadOnlyCampaign) {
      return null;
    }
    // 綁定和新增都是「再讓一份素材進到這個活動」，同一條限制要一起擋；只擋綁定的話，
    // 從新增素材那條路建出來的素材一樣會綁進來而且是啟用的，限制等於白做。
    const canBind = model.canBind();

    const renderWithBlockedTip = (
      tipId: string,
      target: 'bind' | 'create',
      button: React.ReactNode
    ) => (
      <div className={this.cssClassNames('btnWithTip', { block: !canBind })}>
        {!canBind && (
          <div className={styles.tooltip} id={tipId}>
            {model.getBindBlockedTip(target)}
          </div>
        )}
        {button}
      </div>
    );

    const renderBindBtn = () =>
      renderWithBlockedTip(
        BIND_BLOCKED_TIP_ID,
        'bind',
        <button
          className={`btn btn-tertiary btn-sm ${styles.bind}`}
          aria-disabled={!canBind}
          aria-describedby={canBind ? undefined : BIND_BLOCKED_TIP_ID}
          onClick={canBind ? model.l3ObjectStateFn : undefined}
        >
          {i18n.t('creativeOverviewStateContent.buttons.bind')}
        </button>
      );

    const renderCreateCreativeBtn = () => {
      const createCss = this.cssClassNames(
        'btn',
        'btn-secondary',
        'btn-sm',
        'new'
      );
      return renderWithBlockedTip(
        CREATE_BLOCKED_TIP_ID,
        'create',
        <button
          className={createCss}
          aria-disabled={!canBind}
          aria-describedby={canBind ? undefined : CREATE_BLOCKED_TIP_ID}
          onClick={canBind ? model.goCreateCreativeFn : undefined}
        >
          {i18n.t('creativeOverviewStateContent.buttons.new')}
        </button>
      );
    };

    return (
      <PermissionChecker permissionAware={model.bindPermissionAware}>
        <div className={styles.operationArea}>
          {renderBindBtn()}
          {model.showCreateCreativeBtn && renderCreateCreativeBtn()}
        </div>
      </PermissionChecker>
    );
  }

  getChangeCreativeStateOperations(model: CreativeOverviewStateContentModel) {
    const canActive = model.canActive();
    const canDeactive = model.canDeactive();
    const activeClass = this.cssClassNames('activateBtn', {
      block: !canActive
    });

    const deactiveClass = this.cssClassNames('deactivateBtn', {
      block: !canDeactive
    });
    return (
      <div className={styles.batchOperationArea}>
        <Tags
          value={[
            i18n.t('creativeOverviewStateContent.tags.selectedTip', {
              count: model.state.selectedCreatives.length
            })
          ]}
          onChange={model.handleOnUnSelectAll}
        />
        {!model.isReadOnlyCampaign && (
          <div
            className={activeClass}
            onClick={canActive ? model.activeCreatives : undefined}
          >
            {!canActive && (
              <div className={styles.tooltip}>
                {model.getActivateBlockedTip()}
              </div>
            )}
            <span>{i18n.t('creativeOverviewStateContent.buttons.active')}</span>
          </div>
        )}
        <div
          className={deactiveClass}
          onClick={canDeactive ? model.deactiveCreatives : undefined}
        >
          {!canDeactive && (
            <div className={styles.tooltip}>
              {model.campaignInfo.isPmp
                ? i18n.t('creativeOverviewStateContent.buttons.pmpDeactiveTips')
                : i18n.t('creativeOverviewStateContent.buttons.actionTips')}
            </div>
          )}
          <span>{i18n.t('creativeOverviewStateContent.buttons.deactive')}</span>
        </div>
      </div>
    );
  }

  render() {
    const model = this.props.model;
    const state = model.state;
    return (
      <StateDataContext.Consumer>
        {(stateContext: CreativeManagementStateContext) => {
          model.setStateContext(stateContext);
          return (
            <div className={styles.creativeOverviewStateContent}>
              {state.loading && <LoadingIndicator />}
              {state.selectedCreatives.length > 0
                ? this.getChangeCreativeStateOperations(model)
                : this.getAddCreativeOprations(model)}
              <div className={styles.creatives}>
                <CreativeList model={model.getCreativeListModel()} />
              </div>
            </div>
          );
        }}
      </StateDataContext.Consumer>
    );
  }
}
